import { Injectable, OnDestroy } from '@angular/core';
import { LoadingController } from '@ionic/angular';
import { AuthenticationFacade } from '@symphony-solo/domain/authentication/src';
import { CartFacade } from '@symphony-solo/domain/cart/src';
import { ChooseLocationFacade } from '@symphony-solo/domain/choose-location/src';
import { AccountSettingsFacade } from '@symphony-solo/domain/account-settings/src';
import { CustomerGroupsFacade } from '@symphony-solo/domain/customer-groups/src';
import { LocationFacade } from '@symphony-solo/domain/location/src';
import { OrderFacade } from '@symphony-solo/domain/order/src';
import { HistoryFacade } from '@symphony-solo/domain/history/src';
import { PaymentCardsFacade } from '@symphony-solo/domain/payment-cards/src';
import { LoyaltyCardsFacade } from '@symphony-solo/domain/loyalty-cards/src';
import { PurchaseHistoryFacade } from '@symphony-solo/domain/purchase-history/src';
import { ScannerFacade } from '@symphony-solo/domain/scanner/src';
import { SelectProductFacade } from '@symphony-solo/domain/select-product/src';
import { SyncDataFacade } from '@symphony-solo/domain/sync-data/src';
import { TermsAndConditionsFacade } from '@symphony-solo/domain/terms-and-conditions/src';
import { TransactionReceiptFacade } from '@symphony-solo/domain/transaction-receipt/src';
import { TransactionFacade } from '@symphony-solo/domain/transaction/src';
import { VoucherFacade } from '@symphony-solo/domain/voucher/src';
import { LoyaltyWebsiteFacade } from '@symphony-solo/pages/loyalty-website/src';
import { anyTrue } from '@symphony-solo/shared/utils/src';
import { ObservableInput, Subscription } from 'rxjs';
import { concatMap, distinctUntilChanged, withLatestFrom } from 'rxjs/operators';
import { PursesFacade } from '@symphony-solo/domain/purses/src';
import { CompanyGroupFacade } from '@symphony-solo/domain/company-group/src';
import { FavouriteItemFacade } from '@symphony-solo/domain/favourite-item/src';

@Injectable()
export class LoadingSpinnerService implements OnDestroy {
  private loadingElement: HTMLIonLoadingElement = null;
  private subscription: Subscription;

  public constructor(
    private authFacade: AuthenticationFacade,
    private paymentCardsFacade: PaymentCardsFacade,
    private loyaltyCardsFacade: LoyaltyCardsFacade,
    private loyaltyWebsiteFacade: LoyaltyWebsiteFacade,
    private transactionFacade: TransactionFacade,
    private customerGroupsFacade: CustomerGroupsFacade,
    private termsAndConditionsFacade: TermsAndConditionsFacade,
    private transactionReceiptFacade: TransactionReceiptFacade,
    private purchaseHistoryFacade: PurchaseHistoryFacade,
    private historyFacade: HistoryFacade,
    private selectProductFacade: SelectProductFacade,
    private locationFacade: LocationFacade,
    private syncDataFacade: SyncDataFacade,
    private loadingController: LoadingController,
    private scannerFacade: ScannerFacade,
    private cartFacade: CartFacade,
    private orderFacade: OrderFacade,
    private voucherFacade: VoucherFacade,
    private chooseLocationFacade: ChooseLocationFacade,
    private accountSettingsFacade: AccountSettingsFacade,
    private companyGroupFacade: CompanyGroupFacade,
    private pursesFacade: PursesFacade,
    private favouriteItemFacade: FavouriteItemFacade,
  ) {}

  public startListenToLoading(): void {
    this.subscription = this.listenToLoading([
      this.loyaltyCardsFacade.isLoading$,
      this.paymentCardsFacade.isLoading$,
      this.loyaltyWebsiteFacade.isLoading$,
      this.transactionFacade.isLoading$,
      this.customerGroupsFacade.isLoading$,
      this.termsAndConditionsFacade.isLoading$,
      this.transactionReceiptFacade.isLoading$,
      this.purchaseHistoryFacade.isLoading$,
      this.authFacade.isLoading$,
      this.selectProductFacade.isLoading$,
      this.locationFacade.isLoading$,
      this.syncDataFacade.isLoading$,
      this.orderFacade.isLoading$,
      this.voucherFacade.isLoading$,
      this.cartFacade.isLoading$,
      this.chooseLocationFacade.isLoading$,
      this.accountSettingsFacade.isLoading$,
      this.historyFacade.isLoading$,
      this.pursesFacade.isLoading$,
      this.companyGroupFacade.isLoading$,
      this.favouriteItemFacade.isLoading$,
    ]);
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private listenToLoading(observables: ObservableInput<boolean>[]): Subscription {
    return anyTrue(...observables)
      .pipe(
        distinctUntilChanged(),
        withLatestFrom(this.scannerFacade.isScannerShown$),
        concatMap(async ([isLoading, isScannerShown]) => {
          const cssClass = ['custom-spinner', isScannerShown ? 'bottom-spinner' : ''];
          if (isLoading) {
            this.loadingElement = await this.loadingController.create({
              backdropDismiss: false,
              spinner: 'circular',
              cssClass,
            });
            await this.loadingElement.present();
          } else {
            if (this.loadingElement) {
              await this.loadingElement.dismiss();
              this.loadingElement = null;
            }
          }
        })
      )
      .subscribe();
  }
}
